import React from 'react';
import { useTranslation } from 'localization';
import type { ContextLevel } from 'types/context';
import { getLocalizedText } from '../../../utils/getLocalizedText';
import { readFilterStringValues } from '../../../utils/readFilterStringValues';
import './ContextLevelSummaryAtom.scss';

export interface ResolvedTypeInfo {
  displayName: string;
  avatar?: string;
}

export interface ContextLevelSummaryAtomProps {
  level: ContextLevel;
  resolvedTypes?: Map<string, ResolvedTypeInfo>;
  compact?: boolean;
  className?: string;
  'data-testid'?: string;
}

function formatSystemName(s: string): string {
  if (typeof s !== 'string') return '';
  return s.replace(/^center_/, '').replaceAll('_', ' ').replaceAll(/\b\w/g, (c) => c.toUpperCase());
}

function getTypeNames(level: ContextLevel, resolvedTypes?: Map<string, ResolvedTypeInfo>): string[] {
  const names: string[] = [];
  for (const f of level.filters?.propertyFilters || []) {
    if (f.property !== 'type') continue;
    names.push(...readFilterStringValues(f));
  }
  return names.map((n) => resolvedTypes?.get(n)?.displayName || formatSystemName(n));
}

const ContextLevelSummaryAtom: React.FC<ContextLevelSummaryAtomProps> = ({
  level, resolvedTypes, compact = false, className, 'data-testid': testId,
}) => {
  const { t } = useTranslation();
  const label = getLocalizedText(level.label);
  const typeNames = getTypeNames(level, resolvedTypes);
  const filterCount = (level.filters?.propertyFilters?.length || 0)
    + (level.filters?.perspectiveFilters?.length || 0)
    + (level.filters?.filterGroups?.length || 0);

  return (
    <div className={['context-level-summary', className].filter(Boolean).join(' ')} data-testid={testId}>
      <div className="context-level-summary__label">
        {label || t('marketplace.approachExplorer.contextSummary.untitledLevel')}
      </div>
      <div className="context-level-summary__types">
        {(() => {
          if (compact && filterCount > 0) {
            return (
              <span className="context-level-summary__filter-count">
                {t('marketplace.approachExplorer.contextSummary.filters', { count: filterCount })}
              </span>
            );
          }
          if (typeNames.length > 0) {
            return typeNames.map((name) => (
              <span key={name} className="context-level-summary__type-name">{name}</span>
            ));
          }
          return (
            <span className="context-level-summary__all-spheres">
              {t('marketplace.approachExplorer.contextSummary.allSpheres')}
            </span>
          );
        })()}
      </div>
      {(level.repeat ?? 1) > 1 && (
        <span className="context-level-summary__repeat" data-testid="repeat-badge">
          {t('marketplace.approachExplorer.contextSummary.repeat', { count: level.repeat })}
        </span>
      )}
    </div>
  );
};

ContextLevelSummaryAtom.displayName = 'ContextLevelSummaryAtom';

export default ContextLevelSummaryAtom;
